import { formatCurrency } from '@erp/shared-utils';

/**
 * Shared discount value formatting for the Discounts v2 surfaces (groups
 * table, side panel, flat elements list, review page). Discount TYPE and
 * AMOUNT render in separate columns, and amounts carry thousands separators.
 */

export function discountTypeLabel(discountType: string): string {
  switch (discountType) {
    case 'FIXED_PRICE':
      return 'Fixed price';
    case 'PERCENT_BPS_OFF':
      return 'Percent off';
    case 'AMOUNT_OFF':
      return 'Amount off';
    default:
      return discountType;
  }
}

/** "$1,234.50" for money values, "20.5%" for percent (value arrives in bps). */
export function discountAmountLabel(
  discountType: string,
  discountValue: number | string,
): string {
  const value = Number(discountValue);
  if (!Number.isFinite(value)) return '—';
  if (discountType === 'PERCENT_BPS_OFF') {
    return `${(value / 100).toLocaleString('en-US', {
      maximumFractionDigits: 2,
    })}%`;
  }
  return formatCurrency(value);
}

/**
 * The effective discount vs the customer list price, e.g. "37.5%": the
 * percent itself for PERCENT_BPS_OFF (bps in), else derived from the list
 * price (fixed price → 1 − price/list; amount off → amount/list). Returns
 * null when it can't be derived (no usable list price).
 */
export function discountPercentLabel(
  discountType: string,
  discountValue: number | string,
  listPrice?: number | string | null,
): string | null {
  const value = Number(discountValue);
  if (!Number.isFinite(value)) return null;
  if (discountType === 'PERCENT_BPS_OFF') {
    return `${(value / 100).toLocaleString('en-US', {
      maximumFractionDigits: 2,
    })}%`;
  }
  const list = listPrice != null ? Number(listPrice) : NaN;
  if (!Number.isFinite(list) || list <= 0) return null;
  const percent =
    discountType === 'AMOUNT_OFF'
      ? (value / list) * 100
      : ((list - value) / list) * 100;
  return `${percent.toLocaleString('en-US', { maximumFractionDigits: 1 })}%`;
}

/** "1,234" quantities (usage / max-quantity cells). */
export function formatQuantity(value: number | string | null): string {
  if (value == null) return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

/** One side of an effective window: the date, or "…" for an open end. */
function windowBound(value: string | null): string {
  return value ?? '…';
}

/** "2026-01-01 → …" effective-window cell; "—" when the window is open-ended on both sides. */
export function effectiveWindowLabel(
  start: string | null,
  end: string | null,
): string {
  if (!start && !end) return '—';
  return `${windowBound(start)} → ${windowBound(end)}`;
}

/** "3 / 10" used-vs-max usage cell (max omitted when unlimited). */
export function usageLabel(
  used: number | string | null,
  max: number | string | null,
): string {
  const usedLabel = formatQuantity(used);
  return max != null ? `${usedLabel} / ${formatQuantity(max)}` : usedLabel;
}
